/**
 * Display formatting for stored ESG values.
 *
 * Shows the stored value at the precision the backend keeps (Decimal(,6)), so a real
 * value like 10.568942 is preserved rather than rounded to 10.57 — reviewers asked to
 * see the original value, not a 2-decimal rounding (Maxine ④). The stored value is
 * never changed (the exact original stays in the record/audit payload); this is
 * presentation only.
 */

/**
 * Max fraction digits shown — the precision the backend STORES (Decimal(,6)), so a
 * real value like 10.568942 is preserved. `toLocaleString` still drops trailing zeros
 * (5 → "5") and rounds to 6 places; genuinely noisy extraction artifacts (e.g.
 * "13200.000000000002") are cleaned upstream at extraction, not hidden here.
 */
const DISPLAY_MAX_FRACTION = 6;

/**
 * Formats a stored numeric string for display: up to 6 decimals (the stored
 * precision) with thousands grouping; small magnitudes keep enough fraction digits
 * that a factor like 0.00108 is not lost. Blanks render as an em dash; non-numeric
 * strings (dates, qualitative verdicts) pass through unchanged.
 * @param {string | null} value The stored value string, or null.
 * @returns {string} The display-formatted value.
 */
export function formatDisplayValue(value: string | null): string {
  if (value === null || value.trim() === '') {
    return '—';
  }
  const numeric = Number(value.replace(/,/g, ''));
  if (!Number.isFinite(numeric)) {
    return value;
  }
  const abs = Math.abs(numeric);
  const maxFrac =
    abs >= 1 || abs === 0
      ? DISPLAY_MAX_FRACTION
      : Math.min(DISPLAY_MAX_FRACTION, Math.max(2, -Math.floor(Math.log10(abs)) + 2));
  return numeric.toLocaleString('en-US', { maximumFractionDigits: maxFrac });
}

/**
 * One-line summary of the sheets extraction skipped ("Sheet2: empty · Sheet3:
 * image-only"). Shared by the admin file list and the review file filter so the
 * coverage-honesty copy cannot drift between surfaces.
 * @param {Array<{ sheet: string; reason: string }>} skipped The skipped sheets.
 * @returns {string} The joined summary line.
 */
export function formatSkippedSheets(skipped: Array<{ sheet: string; reason: string }>): string {
  return skipped.map((entry) => `${entry.sheet}: ${entry.reason}`).join(' · ');
}
